import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setToggleMenu } from "../../server/CartsOpen";
import Navigator from "./NAVIGATOR";
import HeaderConnection from "./HeaderConnection";
import { Menu, X } from "lucide-react";

function measureParentCategoriesBottomPx() {
  const mainCats = document.querySelector(".navigator nav.main");
  // offsetHeight is 0 when the element is display:none (mobile) — fall back to header
  if (mainCats && mainCats.offsetHeight > 0)
    return mainCats.getBoundingClientRect().bottom;
  const wrap = document.querySelector(".header-wrapper");
  return wrap ? wrap.getBoundingClientRect().bottom : 0;
}

function measureControlsBottomPx() {
  return measureParentCategoriesBottomPx();
}

function measureSubNavBottomPx() {
  const subnavs = Array.from(document.querySelectorAll("nav.sub-navbar"));
  if (!subnavs.length) return 0;
  return Math.max(...subnavs.map((n) => n.getBoundingClientRect().bottom));
}

const useDesktopMedia = () => {
  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1024px)").matches,
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const fn = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  return isDesktop;
};

const Header = ({
  setAllProduct,
  currentSubCategory,
  setProductPopUp,
  setSelectedProd,
  setPendingScrollSlug,
  pagePlace,
  setPagePlace,
  haveGetToken,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  const role = useSelector((state) => state.user.role);
  const stationId = useSelector((state) => state.user.stationId);
  const toggleMenu = useSelector((state) => state.openCart.toggleMenu);
  const { pathname } = useLocation();
  const isDesktop = useDesktopMedia();
  const [headerHidden, setHeaderHidden] = useState(false);

  // Mobile only: hide the header when scrolling down, reveal it on scroll up.
  useEffect(() => {
    if (isDesktop) {
      setHeaderHidden(false);
      return undefined;
    }

    let lastY = window.scrollY;
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        if (Math.abs(y - lastY) > 6) {
          if (y > lastY && y > 80) {
            setHeaderHidden(true);
          } else if (y < lastY) {
            setHeaderHidden(false);
          }
          lastY = y;
        }
        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isDesktop]);

  useEffect(() => {
    const collapsedDesktop =
      isDesktop &&
      (pathname.includes("/category/") ||
        pathname.includes("/search") ||
        pathname.includes("/sales"));
    document.body.classList.toggle(
      "filters-sidebar-collapsed",
      collapsedDesktop,
    );
    return () => {
      document.body.classList.remove("filters-sidebar-collapsed");
    };
  }, [isDesktop, pathname]);

  // Keep CSS custom properties in sync with header height.
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof ResizeObserver === "undefined"
    ) {
      return undefined;
    }
    const root = document.documentElement;
    const wrap = document.querySelector(".header-wrapper");
    if (!wrap) return undefined;

    const sync = () => {
      const parentBottom = Math.ceil(measureParentCategoriesBottomPx());
      const controlsBottom = Math.ceil(measureControlsBottomPx());
      const subBottom = Math.ceil(measureSubNavBottomPx());
      const fixedBottom = Math.max(parentBottom, controlsBottom, subBottom);
      root.style.setProperty(
        "--shell-header-offset",
        `${Math.max(0, controlsBottom)}px`,
      );
      root.style.setProperty(
        "--shell-parent-cats-bottom",
        `${Math.max(0, parentBottom)}px`,
      );
      root.style.setProperty(
        "--shell-controls-bottom",
        `${Math.max(0, controlsBottom)}px`,
      );
      root.style.setProperty(
        "--shell-fixed-stack-bottom",
        `${Math.max(0, fixedBottom)}px`,
      );
    };

    sync();
    const ro = new ResizeObserver(() => requestAnimationFrame(sync));
    ro.observe(wrap);
    window.addEventListener("resize", sync);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [pathname]);

  return (
    <header>
      <div
        className={`header-wrapper${headerHidden ? " header-wrapper--hidden" : ""}`}
      >
        <HeaderConnection
          setAllProduct={setAllProduct}
          user={user}
          role={role}
          stationId={stationId}
          basicHeader={false}
          haveGetToken={haveGetToken}
        />
        {pathname !== "/about" ? (
          <>
            <div className="head-left-side">
              <div className="menu" onClick={() => dispatch(setToggleMenu())}>
                {toggleMenu ? <X aria-hidden /> : <Menu aria-hidden />}
              </div>
              <Navigator
                currentSubCategory={currentSubCategory}
                setPendingScrollSlug={setPendingScrollSlug}
              />
            </div>
          </>
        ) : null}
      </div>
    </header>
  );
};

export default Header;
